import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18n from "../i18n";
import { trackingSlice } from "../trackingSlice";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/reservations/`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const myBookingSlice = createApi({
  reducerPath: "bookingSlice",
  baseQuery: baseQuery,
  tagTypes: ["booking"],
  endpoints: (builder) => ({
    getAllBooking: builder.query({
      query: ({ page, limit, reservation_type, search }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (reservation_type)
          params.append("reservation_type", reservation_type);
        if (search) params.append("search", search);

        return `get-all?${params.toString()}`;
      },

      // inside getReservedVehicles endpoint:
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const bookingData = data?.data?.reservations ?? [];
          if (!Array.isArray(bookingData) || bookingData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const booking of bookingData) {
            if (booking.reservation_type) bucket.push(booking.reservation_type);
            if (booking.repeat_interval) bucket.push(booking.repeat_interval);
            if (booking.booking_type) bucket.push(booking.booking_type);
            if (booking.school.full_name) bucket.push(booking.school.full_name);
            if (booking.school.company_name)
              bucket.push(booking.school.company_name);
            if (booking.vendor.full_name) bucket.push(booking.vendor.full_name);
            if (booking.vendor.company_name) bucket.push(booking.company_name);
            if (booking.vendor.status) bucket.push(booking.vendor.status);
            if (booking.vehicle.vehicle_type_name)
              bucket.push(booking.vehicle.vehicle_type_name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? map.get(s) ?? s : s);

          dispatch(
            myBookingSlice.util.updateQueryData(
              "getAllBooking",
              arg,
              (draft) => {
                const arr = draft?.data?.reservations ?? [];
                arr.forEach((booking) => {
                  booking.reservation_type = pick(booking.reservation_type);
                  booking.repeat_interval = pick(booking.repeat_interval);
                  booking.booking_type = pick(booking.booking_type);
                  booking.school.full_name = pick(booking.school.full_name);
                  booking.school.company_name = pick(
                    booking.school.company_name
                  );
                  booking.vendor.full_name = pick(booking.vendor.full_name);
                  booking.vendor.company_name = pick(
                    booking.vendor.company_name
                  );
                  booking.vendor.status = pick(booking.vendor.status);
                  booking.vehicle.vehicle_type_name = pick(
                    booking.vehicle.vehicle_type_name
                  );
                });
              }
            )
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["booking"],
    }),

    // super_admin-only: immediately completes a trip stuck in
    // started/in_progress (a driver never hit the "end route" popup).
    // Mirrors what the midnight ride-auto-complete cron would eventually
    // do, but on demand.
    forceCloseInstance: builder.mutation({
      query: (instanceId) => ({
        url: `instance/${instanceId}/force-close`,
        method: "POST",
      }),
      async onQueryStarted(_instanceId, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          // trackingSlice is a separate createApi instance — invalidate its
          // "tracking" tag so the active-trips grid drops the closed card.
          dispatch(trackingSlice.util.invalidateTags(["tracking"]));
        } catch {
          /* mutation failed — let the caller's own error handling deal with it */
        }
      },
    }),
  }),
});

export const { useGetAllBookingQuery, useForceCloseInstanceMutation } =
  myBookingSlice;
